import UpgradeType from '@app/store/UpgradeType.js';
import type { EnhancementEntity, EnhancementStoreState } from '@app/store/data/enhancement/enhancement.types';
import { getState, patchState } from '@ngrx/signals';
import { addEntity, removeAllEntities, updateEntity } from '@ngrx/signals/entities';
import { injectDispatch } from '@ngrx/signals/events';
import { enhancementEvents } from '@app/store/data/enhancement/enhancement.effects';
import { sanitizeInput } from '@app/utils/sanitize';

// eslint-disable-next-line max-lines-per-function
export const ENHANCEMENT_METHODS = (store, dispatch = injectDispatch(enhancementEvents)) => ({
  loadTemplateAttempt(templateName: string): void {
    dispatch.loadTemplateAttempt(templateName);
  },
  addAvailableUpgradesByJob(payload: EnhancementEntity): void {
    patchState(store, addEntity(payload));
  },
  setPersonalizationName(payload: any): void {
    const state: EnhancementStoreState = getState(store);
    const entity: EnhancementEntity = state.entityMap[payload.subjectId];
    const changes = personalizationNameUpdate(entity, payload);
    patchState(store, updateEntity({ id: payload.subjectId, changes }));
  },
  setRetouch(payload: any): void {
    const state: EnhancementStoreState = getState(store);
    const entity: EnhancementEntity = state.entityMap[payload.subjectId];
    const changes = retouchUpdate(entity, payload);

    patchState(store, updateEntity({ id: payload.subjectId, changes }));
  },
  loadTemplateSuccess(payload: any): void {
    patchState(store, { templateMap: { ...store.templateMap(), [payload.templateName]: payload } });
  },
  setDefaultEnhancements(payload: any): void {
    const state: EnhancementStoreState = getState(store);
    const entity: EnhancementEntity = state.entityMap[payload.subjectId];
    const { PERSONALIZATION, retouch } = entity?.selectedUpgrades || {};

    if (PERSONALIZATION?.skuCode || retouch) {
      return;
    }
    const changes = defaultEnhancementUpdate(payload);
    patchState(store, updateEntity({ id: payload.subjectId, changes }));
  },
  copyEnhancements(payload: { subjectIdFrom: string; subjectIdTo: string; targetName?: string }): void {
    const state: EnhancementStoreState = getState(store);
    const sourceEntity: EnhancementEntity = state.entityMap[payload.subjectIdFrom];
    if (!sourceEntity) {
      return;
    }
    const sourcePersonalization = sourceEntity.selectedUpgrades?.PERSONALIZATION;
    const personalization =
      payload.targetName && sourcePersonalization
        ? { ...sourcePersonalization, value: payload.targetName }
        : sourcePersonalization;
    const changes: Partial<EnhancementEntity> = {
      selectedUpgrades: { ...sourceEntity.selectedUpgrades, PERSONALIZATION: personalization }
    };
    patchState(store, updateEntity({ id: payload.subjectIdTo, changes }));
  },
  clearEnhancementStore(): void {
    patchState(store, { templateMap: {} });
    patchState(store, removeAllEntities());
  }
});
function defaultEnhancementUpdate(payload: any): Partial<EnhancementEntity> {
  return {
    selectedUpgrades: {
      [UpgradeType.PERSONALIZATION]: { value: sanitizeInput(payload.name), skuCode: payload?.skuCode ?? null },
      retouch: payload?.retouch ?? null
    }
  };
}
function personalizationNameUpdate(entity: EnhancementEntity, payload: any): Partial<EnhancementEntity> {
  return {
    selectedUpgrades: {
      ...(entity.selectedUpgrades || {}),
      PERSONALIZATION: {
        ...entity.selectedUpgrades?.PERSONALIZATION,
        ...payload.value,
        value: sanitizeInput(payload.value?.value)
      }
    }
  };
}
function retouchUpdate(entity: EnhancementEntity, payload: any): Partial<EnhancementEntity> {
  return {
    selectedUpgrades: {
      ...(entity.selectedUpgrades || {}),
      retouch: payload.value
    }
  };
}
